import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Building,
	Clock,
	Crown,
	Edit,
	Image,
	Mail,
	Plus,
	Shield,
	Trash2,
	User,
	Users,
	X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/staff-management",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
						<Users className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.staffManagement")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.staffManagementDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.staffGuideOverviewTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideOverviewText")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.staffGuideKeyFeatures")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.staffGuideKeyFeature1")}</li>
									<li>• {t("help.staffGuideKeyFeature2")}</li>
									<li>• {t("help.staffGuideKeyFeature3")}</li>
									<li>• {t("help.staffGuideKeyFeature4")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Staff Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<User className="h-5 w-5" />
						<span>{t("help.staffGuideAccessingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideAccessingText")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.staffGuideAccessingStep1")}</li>
						<li>{t("help.staffGuideAccessingStep2")}</li>
						<li>{t("help.staffGuideAccessingStep3")}</li>
					</ol>
					<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<Shield className="h-5 w-5 text-warning mt-0.5" />
							<div>
								<h4 className="font-medium text-warning">
									{t("help.staffGuidePermissionTitle")}
								</h4>
								<p className="text-sm text-warning/80 mt-1">
									{t("help.staffGuidePermissionText")}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideAccessingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Understanding Roles and Permissions */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Crown className="h-5 w-5" />
						<span>{t("help.staffGuideRolesTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideRolesText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideRolesAvailableTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Crown className="h-4 w-4 mr-2 text-yellow-600" />
											{t("help.staffGuideRoleOwnerTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleOwnerText")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Shield className="h-4 w-4 mr-2 text-red-600" />
											{t("help.staffGuideRoleAdministratorTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleAdministratorText")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Users className="h-4 w-4 mr-2 text-blue-600" />
											{t("help.staffGuideRoleManagerTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleManagerText")}
										</p>
									</div>
								</div>
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<User className="h-4 w-4 mr-2 text-green-600" />
											{t("help.staffGuideRoleInstructorTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleInstructorText")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Building className="h-4 w-4 mr-2 text-purple-600" />
											{t("help.staffGuideRoleAccountantTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleAccountantText")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<AlertCircle className="h-4 w-4 mr-2 text-gray-600" />
											{t("help.staffGuideRoleViewerTitle")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideRoleViewerText")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div className="bg-info/10 border border-info/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Shield className="h-5 w-5 text-info mt-0.5" />
								<div>
									<h5 className="font-medium text-info">
										{t("help.staffGuideRolePermissionsMatrixTitle")}
									</h5>
									<p className="text-sm text-info/80 mt-1">
										{t("help.staffGuideRolePermissionsMatrixText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideRolesImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Staff List Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.staffGuideListTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">{t("help.staffGuideListText")}</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideListColumnsTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.staffGuideListColumn1Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideListColumn1Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.staffGuideListColumn2Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideListColumn2Text")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.staffGuideListColumn3Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideListColumn3Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.staffGuideListColumn4Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideListColumn4Text")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideListIndicatorsTitle")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.staffGuideListIndicator1")}</li>
								<li>• {t("help.staffGuideListIndicator2")}</li>
								<li>• {t("help.staffGuideListIndicator3")}</li>
								<li>• {t("help.staffGuideListIndicator4")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideListImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Inviting New Staff Members */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Plus className="h-5 w-5" />
						<span>{t("help.staffGuideInvitingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideInvitingText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideInvitingStep1Title")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.staffGuideInvitingStep1Text1")}</li>
								<li>{t("help.staffGuideInvitingStep1Text2")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideInvitingStep2Title")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.staffGuideInvitingRequiredFieldsTitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.staffGuideInvitingRequiredField1")}</li>
									<li>• {t("help.staffGuideInvitingRequiredField2")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideInvitingStep3Title")}
							</h4>
							<div className="space-y-2">
								<h5 className="font-medium text-sm text-base-content">
									{t("help.staffGuideInvitingRoleSelectionTitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.staffGuideInvitingRoleSelection1")}</li>
									<li>• {t("help.staffGuideInvitingRoleSelection2")}</li>
									<li>• {t("help.staffGuideInvitingRoleSelection3")}</li>
									<li>• {t("help.staffGuideInvitingRoleSelection4")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideInvitingStep4Title")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.staffGuideInvitingStep4Text1")}</li>
								<li>{t("help.staffGuideInvitingStep4Text2")}</li>
								<li>{t("help.staffGuideInvitingStep4Text3")}</li>
								<li>{t("help.staffGuideInvitingStep4Text4")}</li>
							</ol>
						</div>

						<div className="bg-success/10 border border-success/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Mail className="h-5 w-5 text-success mt-0.5" />
								<div>
									<h5 className="font-medium text-success">
										{t("help.staffGuideInvitingDetailsTitle")}
									</h5>
									<ul className="text-sm text-success/80 mt-2 space-y-1">
										<li>• {t("help.staffGuideInvitingDetail1")}</li>
										<li>• {t("help.staffGuideInvitingDetail2")}</li>
										<li>• {t("help.staffGuideInvitingDetail3")}</li>
										<li>• {t("help.staffGuideInvitingDetail4")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideInvitingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Managing Existing Staff */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.staffGuideManagingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideManagingText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideManagingEditingTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.staffGuideManagingEditingStep1")}</li>
								<li>{t("help.staffGuideManagingEditingStep2")}</li>
								<li>{t("help.staffGuideManagingEditingStep3")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideManagingActionsTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Crown className="h-4 w-4 mr-2" />
											{t("help.staffGuideManagingAction1Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideManagingAction1Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Building className="h-4 w-4 mr-2" />
											{t("help.staffGuideManagingAction2Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideManagingAction2Text")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<User className="h-4 w-4 mr-2" />
											{t("help.staffGuideManagingAction3Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideManagingAction3Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Trash2 className="h-4 w-4 mr-2" />
											{t("help.staffGuideManagingAction4Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.staffGuideManagingAction4Text")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
								<div>
									<h5 className="font-medium text-warning">
										{t("help.staffGuideManagingNotesTitle")}
									</h5>
									<ul className="text-sm text-warning/80 mt-2 space-y-1">
										<li>• {t("help.staffGuideManagingNote1")}</li>
										<li>• {t("help.staffGuideManagingNote2")}</li>
										<li>• {t("help.staffGuideManagingNote3")}</li>
										<li>• {t("help.staffGuideManagingNote4")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideManagingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Managing Pending Invitations */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Clock className="h-5 w-5" />
						<span>{t("help.staffGuidePendingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuidePendingText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuidePendingStatusTitle")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.staffGuidePendingStatus1")}</li>
								<li>• {t("help.staffGuidePendingStatus2")}</li>
								<li>• {t("help.staffGuidePendingStatus3")}</li>
								<li>• {t("help.staffGuidePendingStatus4")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuidePendingActionsTitle")}
							</h4>
							<div className="space-y-2">
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<X className="h-4 w-4 mr-2 text-error" />
										{t("help.staffGuidePendingActionTitle")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.staffGuidePendingActionText")}
									</p>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuidePendingAcceptanceTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.staffGuidePendingAcceptanceStep1")}</li>
								<li>{t("help.staffGuidePendingAcceptanceStep2")}</li>
								<li>{t("help.staffGuidePendingAcceptanceStep3")}</li>
								<li>{t("help.staffGuidePendingAcceptanceStep4")}</li>
								<li>{t("help.staffGuidePendingAcceptanceStep5")}</li>
							</ol>
						</div>

						<div className="bg-error/10 border border-error/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Clock className="h-5 w-5 text-error mt-0.5" />
								<div>
									<h5 className="font-medium text-error">
										{t("help.staffGuidePendingExpirationTitle")}
									</h5>
									<p className="text-sm text-error/80 mt-1">
										{t("help.staffGuidePendingExpirationText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuidePendingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Establishment Access Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Building className="h-5 w-5" />
						<span>{t("help.staffGuideEstablishmentTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.staffGuideEstablishmentText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideEstablishmentUnderstandingTitle")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.staffGuideEstablishmentUnderstanding1")}</li>
								<li>• {t("help.staffGuideEstablishmentUnderstanding2")}</li>
								<li>• {t("help.staffGuideEstablishmentUnderstanding3")}</li>
								<li>• {t("help.staffGuideEstablishmentUnderstanding4")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideEstablishmentAssigningTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.staffGuideEstablishmentAssigningStep1")}</li>
								<li>{t("help.staffGuideEstablishmentAssigningStep2")}</li>
								<li>{t("help.staffGuideEstablishmentAssigningStep3")}</li>
								<li>{t("help.staffGuideEstablishmentAssigningStep4")}</li>
								<li>{t("help.staffGuideEstablishmentAssigningStep5")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.staffGuideEstablishmentRemovingTitle")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.staffGuideEstablishmentRemoving1")}</li>
								<li>• {t("help.staffGuideEstablishmentRemoving2")}</li>
								<li>• {t("help.staffGuideEstablishmentRemoving3")}</li>
							</ul>
						</div>

						<div className="bg-info/10 border border-info/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Building className="h-5 w-5 text-info mt-0.5" />
								<div>
									<h5 className="font-medium text-info">
										{t("help.staffGuideEstablishmentBestPracticeTitle")}
									</h5>
									<p className="text-sm text-info/80 mt-1">
										{t("help.staffGuideEstablishmentBestPracticeText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.staffGuideEstablishmentImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
